import { mergeDeep } from "./mergeDeep.js";
/** `SiteContent` key for marketing copy overrides (merged with SITE_COPY_DEFAULT). */
export const SITE_COPY_CONTENT_KEY = "site_copy";
export const SITE_COPY_DEFAULT = {
    common: {
        close: "Close",
    },
    hero: {
        eyebrow: "Live Worship with Dorothy Ashaba",
        titleTogether: "Together ",
        titleIn: "In",
        titleHeaven: "Heaven",
        titleFlame: "Worship Overnight",
        metaTime: "9:30 PM — 5:30 AM",
        venueK: "Venue",
        venueLine1: "2nd Floor · Thobani Center",
        venueLine2: "Kampala, Uganda",
        reserveSeat: "Reserve Your Seat",
        reserveClose: "Close",
        streamLive: "Stream Live",
        countdownLabel: "Begins in",
        posterAlt: "Together In Heaven poster",
    },
    home: {
        features: {
            sectionEyebrow: "Find your way in",
            sectionTitle: "A few doors into the night",
            rows: [
                {
                    eyebrow: "01 — The Night",
                    body: "Eight hours of unbroken worship inside a candle-lit hall. See the program, the venue, and what to expect.",
                    cta: "View the event",
                },
                {
                    eyebrow: "02 — The Voice",
                    title: "Dorothy Ashaba",
                    body: "A worship leader carrying a generation toward the throne. Read her story and the ministry's journey.",
                    cta: "Meet Dorothy",
                },
                {
                    eyebrow: "03 — Tune In",
                    title: "Watch Live",
                    body: "Can't make it to Kampala? Join the global congregation streaming the night in real time.",
                    cta: "Open the stream",
                },
                {
                    eyebrow: "04 — Past Nights",
                    title: "Gallery",
                    body: "Photos and footage from previous overnights — a living archive of glory remembered.",
                    cta: "Browse the gallery",
                },
            ],
        },
        dorothyTeaser: {
            eyebrow: "The voice",
            quote: '"Heaven is not a place we visit on Friday — it\'s the room we build with our worship."',
            body: "Dorothy Ashaba has spent the last decade gathering Kampala under one chorus. Together In Heaven is the night that gathering opens the heavens for everyone who walks in.",
            cta: "Read her story",
            imageAlt: "Dorothy Ashaba leading worship",
        },
        liveCta: {
            pill: "Live · Worship room open",
            statWatchLabel: "watching now",
            statRegLabel: "registered worldwide",
            title: "Step into the room — wherever you are.",
            body: "The livestream opens at 9:30 PM EAT and runs all night. Bring your people, dim the lights, lift the volume.",
            cta: "Open the stream",
        },
        partnerCta: {
            eyebrow: "Partner with us",
            title: "Build the room with us.",
            body: "Sound, lights, livestream, hospitality — every chair filled is held up by partners who believe the night matters. Three ways in.",
            ctaPrimary: "Become a partner",
            ctaGhost: "Talk to the team",
        },
        participation: {
            eyebrow: "your voice in the room",
            title: "Today's invitation",
            lede: "One question, one step — add your voice to the gathering.",
            todayLabel: "Today's invitation",
            exploreAll: "Explore all ways to participate",
            wallEmpty: "Be the first to answer today.",
            wallTitle: "Voices from the room",
            seatsReserved: "seats reserved",
            rsvpYes: "Yes, I'm coming",
            rsvpNotYet: "Not yet — reserve a seat",
            rsvpConfirmed: "You're on the list",
            inviteShared: "I shared an invite",
            prepProgress: "ready for the night",
            pastoralThanks: "Thank you. Someone from our team will reach out within 48 hours.",
            submitThanks: "Thank you — your voice is in the room.",
            pendingReview: "Your answer is awaiting review and will appear here once approved.",
        },
    },
    dorothy: {
        photoUrl: "",
        photoCaption: "Kampala · 2025",
        eyebrow: "the voice",
        name: "Dorothy Ashaba",
        lede: 'A worship leader, psalmist, and pastor whose voice has become a familiar place of refuge for thousands across East Africa. What she offers isn\'t a setlist — it is a sanctuary. She carries a simple conviction: worship is the room where God answers.',
        quote: "We don't plan the night. We prepare a room. He writes the rest.",
        quoteCite: "— Dorothy Ashaba",
    },
    movement: {
        eyebrow: "the movement",
        titleLine1: "Together In Heaven began",
        titleLine2: "as one prayer, sung all night.",
    },
    partner: {
        eyebrow: "partner with us",
        title: "Keep the room open.",
        lede: "Together In Heaven is free to attend and free to stream, which means it is paid for in smaller, quieter gifts — from monthly partners, one-night sponsors, and listeners who send what they can. Your giving pays for sound, lights, livestream bandwidth, and the next recording.",
        tiers: [
            {
                tier: "monthly",
                k: "Monthly",
                v: "UGX 50,000",
                note: "Covers one seat per month, forever.",
                cta: "Become a Partner",
                amountUgx: 50_000,
                variant: "ghost",
            },
            {
                tier: "one_night",
                k: "One Night",
                v: "UGX 250,000",
                note: "Sponsor a full overnight for someone else.",
                cta: "Sponsor a Night",
                amountUgx: 250_000,
                feat: true,
                tag: "Most loved",
                variant: "primary",
            },
            {
                tier: "recording",
                k: "Recording",
                v: "UGX 1,000,000",
                note: "Fund an Upper Room album session.",
                cta: "Fund a Recording",
                amountUgx: 1_000_000,
                variant: "ghost",
            },
        ],
    },
    contact: {
        waEyebrow: "stay close",
        waTitle: "Message us on WhatsApp.",
        waLede: "For questions about the night, partnering, or travel — we answer personally, usually within a few hours.",
        waCardK: "Chat with our team",
        waCardV: "+256 779 809 343",
        formHeading: "Or send us a note",
        newsEyebrow: "the upper room letter",
        newsTitle: "A short letter, once a month.",
        newsLede: "The next event, a song we're singing, and one thing we're praying for. Nothing else.",
    },
    gallery: {
        eyebrow: "from past nights",
        title: "The room remembers.",
        tiles: [
            { cls: "gal-1", label: "WIDE SHOT · ROOM AT 2AM", caption: "Upper Room Vol. 2 · Aug 2024" },
            { cls: "gal-2", label: "HANDS RAISED", caption: "Kampala · Nov 2024" },
            { cls: "gal-3", label: "CANDLE CLOSEUP", caption: "The Quiet Hour · Feb 2025" },
            { cls: "gal-4", label: "DOROTHY ON STAGE", caption: "Thobani Center · Mar 2025" },
            { cls: "gal-5", label: "BAND · WIDE", caption: "Sunrise Set · Dec 2024" },
            { cls: "gal-6", label: "CROWD FROM BALCONY", caption: "Jubilee Night · Jun 2025" },
        ],
    },
    blog: {
        eyebrow: "journal",
        title: "From the ministry",
        lede: "Notes, reflections, and stories from the team behind Together In Heaven.",
        empty: "No posts yet. Check back soon.",
    },
    stream: {
        eyebrow: "the broadcast",
        title: "Worship with us, from anywhere.",
        lede: "The whole night streams live — free, unbroken, in HD. Save the link and join when your hour comes.",
        liveBadge: "LIVE IN —",
        playerTitle: "Together In Heaven · Worship Overnight",
        playerSub: "Dorothy Ashaba & the Upper Room Band",
        metaWatch: "watching now",
        metaReg: "registered",
        metaCountries: "countries",
        share: "Share",
        calendar: "Add to Calendar",
    },
    eventSection: {
        eyebrow: "the gathering",
        title: "A night where earth grows quiet\nand heaven leans in.",
        lede: "For eight hours, under a canopy of worship in the heart of Kampala, we gather to lift one unbroken song — from the last light of Friday to the first light of Saturday. No performance. No pretense. Just the saints, the Spirit, and a voice you know.",
    },
    live: {
        offlineNote: "No live broadcast right now — enjoy the most recent overnight while you wait. We'll switch back to live the moment we go on air.",
        playlistTitle: "Recent overnights",
        commentPendingNote: "Thank you — your comment was received and will appear after a moderator approves it.",
    },
    participate: {
        eyebrow: "join the conversation",
        title: "Ways to participate",
        lede: "Share your story, prepare for the night, or walk with us through prayer — your voice belongs here.",
        eventPrepTitle: "Getting ready for the overnight",
        featuredToday: "Featured today",
        ministryTitle: "Ministry prompts",
    },
};
export function mergeSiteCopy(patch) {
    const base = structuredClone(SITE_COPY_DEFAULT);
    return mergeDeep(base, patch);
}
//# sourceMappingURL=siteCopy.js.map